// Implements: F-MCK-001, F-UIX-023, F-UIX-008, F-UIX-009, F-UIX-007, F-UIX-017, F-CNV-012
/**
 * The Lienzo's creation hero (UX-04): the form that creates a mockup. Pure presentation.
 *
 * The mockup's `.prompt` card: a multi-line description (Ctrl+Enter sends, Enter writes a new
 * line), the optional reference image behind the clip button, the optional name, the scope chip
 * and "Crear mockup"; under it, suggestion chips that fill the description. The typed text, the
 * chosen scope and the attached image are props: a refused creation must leave them where they
 * were (F-UIX-008), and only the container knows it was refused.
 *
 * The scope chip is a native `<select>` dressed as the mockup's chip (decision D-UX-15): it is
 * keyboard-operable, announced and mobile-friendly for free, the same field the old form had.
 */

import { useRef } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Kbd } from "../../../shared/ui/atoms/Kbd";
import { Textarea } from "../../../shared/ui/atoms/Textarea";
import { Tooltip } from "../../../shared/ui/molecules/Tooltip";
import styles from "./MockupCreateForm.module.css";
import { SCOPE_FIELD_ID } from "./ScopeSelector";
import type { ScopeOption } from "./scopeOptions";

export const FIRST_MESSAGE_FIELD_ID = "create-first-message";
export const NAME_FIELD_ID = "create-name";
export const IMAGE_FIELD_ID = "create-reference-image";
const SCOPE_HINT_ID = `${SCOPE_FIELD_ID}-hint`;
const SCOPE_EXPLAIN_ID = `${SCOPE_FIELD_ID}-explain`;
const NAME_HINT_ID = `${NAME_FIELD_ID}-hint`;
const IMAGE_ERROR_ID = `${IMAGE_FIELD_ID}-error`;

/** Why a chosen file was not attached: only the quick pre-checks; the server judges again. */
export type CreateImageRefusal = "notSupported" | "tooBig" | "unreadable";

const IMAGE_REFUSALS = {
  notSupported: "conversation.composer.image.notSupported",
  tooBig: "conversation.composer.image.tooBig",
  unreadable: "conversation.composer.image.unreadable",
} as const satisfies Record<CreateImageRefusal, TranslationKey>;

/** The mockup's suggestion chips, in its order. */
const SUGGESTIONS = [
  "lienzo.suggest.banking",
  "lienzo.suggest.signup",
  "lienzo.suggest.landing",
  "lienzo.suggest.sales",
  "lienzo.suggest.checkout",
] as const satisfies readonly TranslationKey[];

export interface CreateImage {
  /** The attached file's name, `null` when none is attached. */
  readonly name: string | null;
  /** Why the last chosen file was not attached. */
  readonly refusal: CreateImageRefusal | null;
  readonly onSelect: (file: File) => void;
  readonly onRemove: () => void;
}

export interface MockupCreateFormProps {
  readonly firstMessage: string;
  readonly onFirstMessageChange: (value: string) => void;
  readonly name: string;
  readonly onNameChange: (value: string) => void;
  readonly scopeOptions: readonly ScopeOption[];
  readonly scope: string;
  readonly onScopeChange: (value: string) => void;
  readonly onSubmit: () => void;
  /** A creation is in flight: nothing can be sent until it answers. */
  readonly pending: boolean;
  /**
   * Why there is no scope to create it in: the scope field and the submit are disabled and the
   * field is described by this sentence. The creation page always offers the personal scope, so
   * it passes none.
   */
  readonly blockedBecause?: string | undefined;
  /** A rate-limit wait is running (shown by the container): nothing can be sent until it ends. */
  readonly waiting?: boolean | undefined;
  /** The optional reference image of the first message (F-CNV-012); absent, none is offered. */
  readonly image?: CreateImage | undefined;
  /** A chip placed by the page next to the scope chip (the design the mockup will wear). */
  readonly designChip?: ReactNode;
}

export function MockupCreateForm({
  firstMessage,
  onFirstMessageChange,
  name,
  onNameChange,
  scopeOptions,
  scope,
  onScopeChange,
  onSubmit,
  pending,
  blockedBecause,
  waiting = false,
  image,
  designChip,
}: MockupCreateFormProps): ReactElement {
  const { t } = useTranslation();
  const message = useRef<HTMLTextAreaElement | null>(null);
  const blank = firstMessage.trim() === "";
  const blocked = blank || waiting || blockedBecause !== undefined;
  // One seat is no choice: it is shown as what it is, with the reason there is nothing to pick.
  const single = scopeOptions.length === 1 && blockedBecause === undefined ? scopeOptions[0] : undefined;
  const refusal = image?.refusal ?? null;
  const imageError = refusal === null ? undefined : t(IMAGE_REFUSALS[refusal]);

  return (
    <>
      <form
        className={styles.prompt}
        noValidate
        onSubmit={(event) => {
          // Inside the application, never a native navigation (DD22). A blank message
          // cannot arrive here: the submit button is disabled, and a browser does
          // not submit implicitly through a disabled default button.
          event.preventDefault();
          onSubmit();
        }}
      >
        <label className={styles.srOnly} htmlFor={FIRST_MESSAGE_FIELD_ID}>
          {t("create.message.label")}
        </label>
        <Textarea
          id={FIRST_MESSAGE_FIELD_ID}
          variant="bare"
          autoGrow
          inputRef={message}
          value={firstMessage}
          onChange={onFirstMessageChange}
          onSubmitShortcut={() => {
            if (!blocked && !pending) onSubmit();
          }}
          placeholder={t("lienzo.hero.placeholder")}
          disabled={pending}
        />
        {image?.name == null ? null : (
          <p className={styles.file}>
            <Icon name="clip" size="sm" />
            <span>{image.name}</span>
            <Button variant="ghost" size="sm" disabled={pending} onClick={image.onRemove}>
              {t("conversation.composer.image.remove")}
            </Button>
          </p>
        )}
        <div className={styles.bar}>
          {image === undefined ? null : (
            <label className={styles.attach} title={t("conversation.composer.image.hint")}>
              <Icon name="clip" />
              <input
                id={IMAGE_FIELD_ID}
                className={styles.srOnly}
                type="file"
                accept="image/png,image/jpeg"
                aria-label={t("conversation.composer.image.label")}
                aria-describedby={imageError === undefined ? undefined : IMAGE_ERROR_ID}
                disabled={pending}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Cleared so choosing the same file again (after removing it) fires a change.
                  const input = event.target;
                  if (file !== undefined) image.onSelect(file);
                  input.value = "";
                }}
              />
            </label>
          )}
          <label className={styles.srOnly} htmlFor={NAME_FIELD_ID}>
            {t("create.name.label")}
          </label>
          <span className={styles.name}>
            <Input
              id={NAME_FIELD_ID}
              value={name}
              onChange={onNameChange}
              placeholder={t("lienzo.hero.namePlaceholder")}
              describedBy={NAME_HINT_ID}
              disabled={pending}
            />
          </span>
          <span id={NAME_HINT_ID} className={styles.srOnly}>
            {t("create.name.hint")}
          </span>
          {single === undefined ? (
            <span className={styles.chip} title={t("scope.explain")}>
              <Icon name="users" size="sm" />
              {/* Owner round 3: the chip reads "Ámbito: …" and says what a scope is. */}
              <span aria-hidden="true">{`${t("scope.label")}:`}</span>
              <span id={SCOPE_EXPLAIN_ID} className={styles.srOnly}>
                {t("scope.explain")}
              </span>
              <select
                id={SCOPE_FIELD_ID}
                className={styles.chipSelect}
                aria-label={t("scope.label")}
                value={scope}
                disabled={pending || blockedBecause !== undefined}
                aria-describedby={blockedBecause === undefined ? SCOPE_EXPLAIN_ID : SCOPE_HINT_ID}
                onChange={(event) => {
                  onScopeChange(event.target.value);
                }}
              >
                {scopeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Icon name="chevron-down" size="sm" />
            </span>
          ) : (
            <Tooltip content={`${t("scope.explain")} ${t("create.scope.only")}`}>
              <button
                type="button"
                className={[styles.chip, styles.chipReadOnly].join(" ")}
                aria-disabled="true"
                aria-label={t("create.scope.only.label")}
              >
                <Icon name="users" size="sm" />
                <span className={styles.chipStatic}>{t("scope.chip.label", { name: single.label })}</span>
              </button>
            </Tooltip>
          )}
          {designChip}
          <span className={styles.spacer} />
          <span className={styles.keys} aria-hidden="true">
            <Kbd>{t("app.kbd.ctrl")}</Kbd> <Kbd>{t("app.kbd.enter")}</Kbd>
          </span>
          <Button variant="primary" type="submit" loading={pending} disabled={blocked}>
            {t("create.submit")}
          </Button>
        </div>
        {blockedBecause === undefined ? null : (
          <p id={SCOPE_HINT_ID} className={styles.help}>
            {blockedBecause}
          </p>
        )}
        {imageError === undefined ? null : (
          <p id={IMAGE_ERROR_ID} className={styles.error} role="alert">
            {imageError}
          </p>
        )}
      </form>
      <div className={styles.suggest} role="group" aria-label={t("lienzo.suggest.label")}>
        {SUGGESTIONS.map((key) => (
          <button
            key={key}
            type="button"
            className={styles.suggestion}
            disabled={pending}
            onClick={() => {
              onFirstMessageChange(t(key));
              message.current?.focus();
            }}
          >
            {t(key)}
          </button>
        ))}
      </div>
    </>
  );
}
